import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, IconButton, Typography, SwipeableDrawer, Box, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import { FaTimes } from 'react-icons/fa';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

const Puller = styled('div')(({ theme }) => ({
  width: 30,
  height: 4,
  backgroundColor: theme.palette.mode === 'light' ? '#888888' : '#ffffff',
  borderRadius: 3,
  position: 'absolute',
  top: 8,
  left: 'calc(50% - 15px)',
}));

const NoImageMessage = () => (
  <div className="flex flex-col items-center justify-center h-full text-center p-4">
    <Typography variant="h6" className="text-gray-300 mb-2">No Receipt Image Available</Typography>
    <Typography variant="body2" className="text-gray-400">
      This receipt was uploaded before we started storing images. All future uploads will include images!
    </Typography>
  </div>
);

// Function to render the appropriate media based on MIME type
const MediaRenderer = ({ src, mimeType, alt, style }) => {
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    // Reset loading state when source changes
    setIsLoading(true);
  }, [src]);

  if (!src) return <NoImageMessage />;

  if (mimeType && mimeType.startsWith('application/pdf')) {
    return (
      <div className="relative w-full h-full flex flex-col items-center">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <CircularProgress />
          </div>
        )}
        <iframe 
          src={`${src}#view=FitH`}
          title="PDF Receipt"
          style={{ 
            ...style,
            display: isLoading ? 'none' : 'block',
            border: 'none',
            width: '100%',
            height: '100%'
          }}
          onLoad={() => setIsLoading(false)}
        />
      </div>
    );
  }

  // Default to image rendering
  return (
    <div className="relative w-full h-full flex items-center justify-center">
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <CircularProgress />
        </div>
      )}
      <img 
        src={src} 
        alt={alt || "Receipt"} 
        style={{ 
          ...style,
          display: isLoading ? 'none' : 'block',
        }}
        onLoad={() => setIsLoading(false)}
      />
    </div>
  );
};

const ImageModal = ({ showImageModal, setShowImageModal, showPreview, setShowPreview, selectedReceipt, receiptImage, isMobile, rightPaneWidth, topOffset }) => {
  const receiptMimeType = selectedReceipt?.mime_type || 'image/jpeg'; // Default to image/jpeg for backwards compatibility
  const isReceiptPDF = receiptMimeType.startsWith('application/pdf');

  if (isMobile) {
    return (
      <Dialog
        open={showImageModal}
        onClose={() => setShowImageModal(false)}
        fullScreen
        PaperProps={{
          style: {
            backgroundColor: '#000',
            boxShadow: 'none',
          },
        }}
      >
        <div className="flex flex-col h-full bg-black">
          <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-b from-black to-transparent p-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                {isReceiptPDF && <PictureAsPdfIcon className="mr-2 text-red-500" />}
                <Typography variant="h6" className="text-white font-semibold">
                  {selectedReceipt?.merchant_name}
                </Typography>
              </div>
              <IconButton 
                onClick={() => setShowImageModal(false)} 
                size="large" 
                style={{ color: 'white' }}
              >
                <CloseIcon />
              </IconButton>
            </div>
          </div>
          <DialogContent className="flex-grow p-0 flex flex-col">
            <div style={{ paddingTop: isReceiptPDF ? '56px' : '0' }} className="w-full h-full">
              <MediaRenderer 
                src={receiptImage} 
                mimeType={receiptMimeType}
                style={{ 
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
              />
            </div>
          </DialogContent>
          <SwipeableDrawer
            anchor="bottom"
            open={true}
            onClose={() => {}}
            onOpen={() => {}}
            disableSwipeToOpen={false}
            disableDiscovery={true}
            PaperProps={{
              style: { 
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                borderTopLeftRadius: '16px',
                borderTopRightRadius: '16px',
              }
            }}
          >
            <div className="text-white p-6">
              <Puller />
              <Typography variant="h6" className="mb-2 font-semibold">{selectedReceipt?.merchant_name}</Typography>
              <Typography variant="body2" className="mb-1 text-gray-300">Date: {selectedReceipt?.date}</Typography>
              <Typography variant="h5" className="mb-3 font-bold">${parseFloat(selectedReceipt?.total_cost || 0).toFixed(2)}</Typography>
              {selectedReceipt?.maybe_receipt_tip && (
                <Typography variant="body2" className="text-gray-300">Tip: ${parseFloat(selectedReceipt.maybe_receipt_tip).toFixed(2)}</Typography>
              )}
              {selectedReceipt?.maybe_receipt_tax && (
                <Typography variant="body2" className="text-gray-300">Tax: ${parseFloat(selectedReceipt.maybe_receipt_tax).toFixed(2)}</Typography>
              )}
            </div>
          </SwipeableDrawer>
        </div>
      </Dialog>
    );
  } else {
    return (
      <div 
        className={`${showPreview ? rightPaneWidth : 'w-0'} flex-shrink-0 bg-gray-900/95 backdrop-blur-sm overflow-y-auto transition-all duration-300 ease-out
          fixed right-0 z-50 border-l border-gray-700/50
          ${showPreview ? 'translate-x-0' : 'translate-x-full'}
        `}
        style={{ 
          width: showPreview ? rightPaneWidth : 0,
          top: topOffset,
          height: `calc(100% - ${topOffset})`
        }}
      >
        {showPreview && (
          <div className="h-full flex flex-col">
            {/* Header */}
            <div className="flex justify-between items-center px-4 py-3 bg-gray-900 border-b border-gray-700/50 sticky top-0 z-50">
              <div className="flex items-center gap-2">
                {isReceiptPDF && <PictureAsPdfIcon className="text-red-400" sx={{ fontSize: 20 }} />}
                <h2 className="text-base font-semibold text-white">Receipt Preview</h2>
              </div>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-400 hover:text-white transition-colors duration-200 p-1.5 rounded-lg hover:bg-gray-800/50"
              >
                <FaTimes size={18} />
              </button>
            </div>
            
            <div className="flex-grow overflow-y-auto">
              {selectedReceipt && (
                <>
                  {/* Receipt Info Card */}
                  <div className="m-3 p-4 bg-gray-800/50 rounded-xl border border-gray-700/50">
                    <h3 className="font-bold text-lg text-white mb-3">{selectedReceipt.merchant_name}</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Date</p>
                        <p className="text-sm text-white">{selectedReceipt.date}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Total</p>
                        <p className="text-sm font-bold text-emerald-400">${parseFloat(selectedReceipt.total_cost).toFixed(2)}</p>
                      </div>
                      {selectedReceipt.maybe_receipt_tax && (
                        <div>
                          <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Tax</p>
                          <p className="text-sm text-white">${parseFloat(selectedReceipt.maybe_receipt_tax).toFixed(2)}</p>
                        </div>
                      )}
                      {selectedReceipt.maybe_receipt_tip && (
                        <div>
                          <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Tip</p>
                          <p className="text-sm text-white">${parseFloat(selectedReceipt.maybe_receipt_tip).toFixed(2)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Receipt Image */}
                  <div className="flex-grow flex justify-center items-start px-3 pb-3 h-[calc(100%-180px)]"
                       style={{ paddingTop: isReceiptPDF ? '12px' : '0' }}>
                    <div className="w-full h-full rounded-xl overflow-hidden border border-gray-700/50 bg-gray-800/30">
                      <MediaRenderer 
                        src={receiptImage} 
                        mimeType={receiptMimeType}
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '100%',
                          width: isReceiptPDF ? '100%' : 'auto',
                          height: isReceiptPDF ? '100%' : 'auto',
                          objectFit: 'contain'
                        }}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
};

export default ImageModal;
